import * as React from "react";
import moment from 'moment';
import { Grid, GridToolbar, GridColumn, GridCellProps, GridSortChangeEvent } from '@progress/kendo-react-grid';
import { Button } from "@progress/kendo-react-buttons";
import { orderBy, SortDescriptor, filterBy, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Switch, Input, Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import {
    IFileStatusReport as ICheckFileStatus, IStateLoading, IDataLoaderInput, AuthorizedProps,
    IFileStatus, IServiceStateOutput, IServiceState, IDropdownData, IWorkingFile, IAuditTracker
} from "../Model";
import { Server } from "../../server/Server";
import { apiConfig } from "../../msal/Config";
import WaitLoading from "../Common/WaitLoading";
import "./ServiceManager.scss";
import { AppAlert } from '../../server/AppAlert';
import { arrowsClockwiseIcon, questionCircleIcon, copyIcon, trashIcon } from '@progress/kendo-svg-icons';

const apiUrls = {
    oneOffLoad: "api/ServiceManager/oneOffLoad",
    oneOffMuleLoad: "api/ServiceManager/muleOneOffLoad",
    checkServiceDisabled: "api/ServiceManager/getServiceStatus",
    setServiceDisabled: "api/ServiceManager/setServiceStatus",
    latestFileStatusList: "api/ServiceManager/latestFileStatusList",
    latestAuditTracker: "api/ServiceManager/latestAuditTracker",
    checkFileStatus: "api/ServiceManager/checkFileStatus",
    getRunningFiles: "api/ServiceManager/getRunningFiles",
    deleteRunningFile: "api/ServiceManager/deleteRunningFile",
    getSettingValue: "api/Lookup/getSettingValue",
    setSettingValue: "api/Lookup/setSettingValue"
}

const settingsKeys = {
    ftpExtractorDisabled: "FtpExtractorDisabled",
    httpExtractorDisabled: "HttpExtractorDisabled",
    folderExtractorDisabled: "FolderExtractorDisabled",
    sftpExtractorDisabled: "SftpExtractorDisabled",
}

const dateFormats = {
    fullFormat: "yyyy-MM-DD HH:mm:ss"
}

const FileStatusDays_Options = [
    { name: 'Last 30 minutes', value: 1. / 48. },
    { name: 'Last hour', value: 1. / 24. },
    { name: 'Last 4 hours', value: 1. / 6. },
    { name: 'Last 12 hours', value: 0.5 },
    { name: 'Last day', value: 1 },
    { name: 'Last 2 days', value: 2 },
    { name: 'Last 3 days', value: 3 },
    { name: 'Last 4 days', value: 4 },
    { name: 'Last 5 days', value: 5 },
    { name: 'Last week', value: 7 },
];

interface IProp extends AuthorizedProps {
}

interface IState extends IStateLoading {
    latestFileStatus: IFileStatus[];
    latestFileStatusView: IFileStatus[];
    fileStatusDays: number;
    runningDays: number;
    dataLoaderInput: IDataLoaderInput;
    fileLoaderDisabled: boolean;
    dataLoaderDisabled: boolean;
    settingsValues: { [key: string]: boolean }
    //ftpExtractorDisabled: boolean;
    //httpExtractorDisabled: boolean;
    //folderExtractorDisabled: boolean;
    intervalId: any;
    sort: SortDescriptor[];
    filter: CompositeFilterDescriptor;
    rSort: SortDescriptor[];
    rFilter: CompositeFilterDescriptor;
    autoRefresh: boolean;
    checkFileName: string;
    fileStatusReport: ICheckFileStatus;
    runningFiles: IWorkingFile[];
    confirmVisible: boolean;
    confirmAction: any;
    confirmTitle?: string;
    tabSelected: number;
    latestMuleFileStatus: IAuditTracker[];
    latestMuleFileStatusView: IAuditTracker[];
}

const RefreshStatusInterval = 90000; // in milliseconds

export default class ServiceManager extends React.Component<IProp, IState> {

    constructor(props: any) {
        super(props);

        this.state = {
            loading: false,
            autoRefresh: false,
            fileStatusDays: 1. / 48.,
            runningDays: 1,
            intervalId: null,
            latestFileStatus: [],
            latestFileStatusView: [],
            fileLoaderDisabled: false,
            checkFileName: '',
            fileStatusReport: {},
            //ftpExtractorDisabled: false,
            //httpExtractorDisabled: false,
            //folderExtractorDisabled: false,
            settingsValues: {
                [settingsKeys.ftpExtractorDisabled]: false,
                [settingsKeys.httpExtractorDisabled]: false,
                [settingsKeys.folderExtractorDisabled]: false,
                [settingsKeys.sftpExtractorDisabled]: false
            },
            sort: [
                {
                    field: "timestamp",
                    dir: "desc"
                }],
            filter: {
                logic: "and",
                filters: []
            },
            rSort: [
                {
                    field: "timestamp",
                    dir: "desc"
                }],
            rFilter: {
                logic: "and",
                filters: []
            },
            confirmVisible: false,
            confirmAction: null,
            dataLoaderDisabled: false,
            dataLoaderInput: {
                name: '', path: 'missed-data', ignoreArchiveCheck: false,
            },
            runningFiles: [],
            tabSelected: 1,
            latestMuleFileStatus: [],
            latestMuleFileStatusView: []
        }
    }

    public async componentDidMount() {
        if (this.state.tabSelected === 0) {
            this.refreshAll();
            this.checkAutoRefresh(this.state.autoRefresh);
        }
        else {
            this.refreshMuleAuditTrackerStatus();
        }
    }

    public componentWillUnmount() {
        if (this.state.intervalId)
            clearInterval(this.state.intervalId);
    }

    public render() {
        return (
            <TabStrip selected={this.state.tabSelected} onSelect={this.handleTabSelect} className="tabstrip">
                <TabStripTab title="Azure GDL">
                    <div className="service-manager row">
                        {this.state.loading && <WaitLoading />}
                        <div className="col-7 file-status">
                            <div className="section-header row">
                                <div className="col-7">
                                    <h6>
                                        GDL Service Status
                                    </h6>
                                </div>
                                <div className="col-3 toolbar-right">
                                    <div className="auto-refresh">
                                        Auto refresh: <Switch onLabel="On" offLabel="Off" id="autoRefresh" className="config-status"
                                            checked={this.state.autoRefresh} onChange={this.toggleAutoRefresh} />
                                    </div>
                                </div>
                                <div className="col-2 toolbar-right">
                                    <Button
                                        title="Refresh all service status and data"
                                        onClick={this.refreshAll}
                                        svgIcon={arrowsClockwiseIcon}
                                        className="cmd refresh"
                                        type="button"
                                    > Refresh all</Button>
                                </div>
                            </div>
                            <div className="section-body row service-status">
                                <div className="col-4">
                                    <label className="status-label-major">File Extractor Service:</label>
                                    <Switch onLabel="Enabled" offLabel="Disabled" id="fileLoaderStatus" className="config-status"
                                        checked={!this.state.fileLoaderDisabled} onChange={this.toggleFileLoaderDisabled} />
                                </div>
                                <div className="col-4">
                                    <label className="status-label-major">Data Loader Service:</label>
                                    <Switch onLabel="Enabled" offLabel="Disabled" id="fileLoaderStatus" className="config-status"
                                        checked={!this.state.dataLoaderDisabled} onChange={this.toggleDataLoaderDisabled} disabled />
                                </div>
                                <div className="col-4 toolbar-right">
                                    <Button
                                        title="Refresh services status"
                                        onClick={this.refreshStatus}
                                        icon="refresh"
                                        svgIcon={arrowsClockwiseIcon}
                                        className="cmd refresh"
                                        type="button">Refresh Status</Button>
                                </div>
                            </div>
                            <div className="section-body row service-status">
                                <div className="col-3">
                                    <label className="status-label">FTP Extractor:</label>
                                    <Switch onLabel="Enabled" offLabel="Disabled" id="ftpExtService" className="config-status"
                                        checked={!this.state.settingsValues[settingsKeys.ftpExtractorDisabled]} onChange={() => this.toggleSettingsDisabled(settingsKeys.ftpExtractorDisabled)} />
                                </div>
                                <div className="col-3">
                                    <label className="status-label">HTTP Extractor:</label>
                                    <Switch onLabel="Enabled" offLabel="Disabled" id="httpExtService" className="config-status"
                                        checked={!this.state.settingsValues[settingsKeys.httpExtractorDisabled]} onChange={() => this.toggleSettingsDisabled(settingsKeys.httpExtractorDisabled)} />
                                </div>
                                <div className="col-3">
                                    <label className="status-label">Folder Extractor:</label>
                                    <Switch onLabel="Enabled" offLabel="Disabled" id="folderExtService" className="config-status"
                                        checked={!this.state.settingsValues[settingsKeys.folderExtractorDisabled]} onChange={() => this.toggleSettingsDisabled(settingsKeys.folderExtractorDisabled)} />
                                </div>
                                <div className="col-3">
                                    <label className="status-label">SFTP Extractor:</label>
                                    <Switch onLabel="Enabled" offLabel="Disabled" id="sftpExtService" className="config-status"
                                        checked={!this.state.settingsValues[settingsKeys.sftpExtractorDisabled]} onChange={() => this.toggleSettingsDisabled(settingsKeys.sftpExtractorDisabled)} />
                                </div>
                            </div>
                            <div className="section-body row service-status">
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <Grid
                                        data={this.state.latestFileStatusView}
                                        className="file-status-grid"
                                        resizable={true}
                                        filterable={true}
                                        filter={this.state.filter}
                                        onFilterChange={this.onFilterChange}
                                        sortable={true}
                                        sort={this.state.sort}
                                        onSortChange={this.sortGrid}
                                    >
                                        <GridToolbar>
                                            <div className="section-header">
                                                <h6>Latest File Status</h6>
                                                <div className="toolbar-right">
                                                    <label className="toolbar-label">Show</label>
                                                    <DropDownList
                                                        onChange={this.changeDayValue}
                                                        name="fileStatusDays"
                                                        textField="name"
                                                        value={this.getDropdownValue(FileStatusDays_Options, this.state.fileStatusDays)}
                                                        data={FileStatusDays_Options}
                                                        className="days-drop-down"
                                                    />
                                                    <Button
                                                        title="Refresh list"
                                                        onClick={() => this.refreshFileStatus()}
                                                        icon="refresh"
                                                        svgIcon={arrowsClockwiseIcon}
                                                        className="cmd refresh"
                                                        type="button"></Button>
                                                </div>
                                            </div>
                                        </GridToolbar>
                                        <GridColumn field="partitionKey" title="Source" className="srcKey" headerClassName="srcKey" resizable sortable />
                                        <GridColumn field="filename" title="File name" className="filename" headerClassName="filename" resizable sortable cells={{ data: this.textCell() }} />
                                        <GridColumn field="length" title="Length" className="size" headerClassName="size" filterable={false} resizable sortable cells={{ data: this.sizeCell() }} />
                                        <GridColumn field="status" title="Status" className="status" headerClassName="status" resizable sortable />
                                        <GridColumn field="elapsed" title="Elapsed" className="elapsed" headerClassName="elapsed" resizable sortable />
                                        <GridColumn field="timestamp" title="Last update" className="dateTime" headerClassName="dateTime" resizable sortable cells={{ data: this.dateCell("yyyy-MM-DD HH:mm") }} />
                                        <GridColumn field="inserted" title="Inserted" className="text-align-right recNum" headerClassName="recNum" resizable filterable={false} sortable />
                                        <GridColumn field="updated" title="Updated" className="text-align-right recNum" headerClassName="recNum" resizable filterable={false} sortable />
                                        <GridColumn field="rejected" title="Rejected" className="text-align-right rejNum" headerClassName="rejNum" resizable filterable={false} sortable />
                                        <GridColumn field="committed" title="Total" className="text-align-right recNum" headerClassName="recNum" resizable filterable={false} sortable />
                                        <GridColumn field="retries" title="Retries" className="text-align-right retries" headerClassName="retries" filterable={false} resizable sortable />
                                        <GridColumn field="startedDateTime" title="Started" className="dateTime" headerClassName="dateTime" resizable sortable cells={{ data: this.dateCell("yyyy-MM-DD HH:mm") }} />
                                    </Grid>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <Grid
                                        data={orderBy(filterBy(this.state.runningFiles, this.state.rFilter), this.state.rSort)}
                                        className="running-files-grid"
                                        resizable={true}
                                        filterable={true}
                                        filter={this.state.rFilter}
                                        sortable={true}
                                        sort={this.state.rSort}
                                        onFilterChange={this.onFilterChangeR}
                                        onSortChange={this.onSortChangeR}
                                    >
                                        <GridToolbar>
                                            <div className="section-header">
                                                <h6>Running files</h6>
                                                <div className="toolbar-right">
                                                    <Button
                                                        title="Refresh list"
                                                        onClick={() => this.refreshRunningFiles()}
                                                        icon="refresh"
                                                        svgIcon={arrowsClockwiseIcon}
                                                        className="cmd refresh"
                                                        type="button"></Button>
                                                </div>
                                            </div>
                                        </GridToolbar>
                                        <GridColumn field="partitionKey" title="Source" width="180px" resizable sortable />
                                        <GridColumn field="filename" title="File name" resizable sortable />
                                        <GridColumn field="timestamp" title="Last Updated" width="185px" filterable={false} resizable sortable cells={{ data: this.dateCell() }} />
                                        <GridColumn field="" title="Actions" width="125px" filterable={false} resizable sortable cells={{ data: this.commandCell() }} />
                                    </Grid>
                                </div>
                            </div>
                        </div>
                        <div className="col-5">
                            <div className="section-header row file-check-header">
                                <div className="col-12 ">
                                    <h6>
                                        GDL File Status Check
                                    </h6>
                                </div>
                            </div>
                            <div className="section-body row">
                                <div className="col-12 file-check">
                                    <div className="row input-filename">
                                        <div className="col-9">
                                            <label htmlFor="checkFileName">Filename:</label>
                                            <Input id="checkFileName" name="checkFileName" className="file-name"
                                                value={this.state.checkFileName}
                                                required={true}
                                                onChange={this.changeCheckFileName}
                                                title="File name to check"
                                                pattern="^\S{9,}$" />
                                        </div>
                                        <div className="col-3">
                                            <Button
                                                title="Check file processing status"
                                                onClick={() => this.checkFileStatus()}
                                                icon="question"
                                                className="cmd check-file-status"
                                                type="button"
                                            > Check file status</Button>
                                        </div>
                                    </div>
                                    <div className="row section-header">
                                        <h6>
                                            Check results
                                        </h6>
                                    </div>
                                    <div className="row">
                                        <div className="col-12 subheader">
                                            <h6 className={!!this.state.fileStatusReport.fileConfig ? "on" : "off"}>File extractor configuration</h6>
                                        </div>
                                    </div>
                                    {this.state.fileStatusReport.fileConfig &&
                                        <><div className="row">
                                            <div className="col-8">
                                                <div><span className="label">File: </span><span> {this.state.fileStatusReport.fileConfig.fileName}</span></div>
                                            </div>
                                            <div className="col-4">
                                                <div><span className="label">File type: </span><span>{this.state.fileStatusReport.fileConfig.fileType}</span></div>
                                            </div>
                                        </div>
                                            <div className="row">
                                                <div className="col-4">
                                                    <div><span className="label">Source key: </span><span>{this.state.fileStatusReport.fileConfig.source.sourceKey}</span></div>
                                                    <div><span className="label">Source name: </span><span>{this.state.fileStatusReport.fileConfig.source.sourceName}</span></div>
                                                    <div><span className="label">Source folder: </span><span>{this.state.fileStatusReport.fileConfig.source.sourceFolder}</span></div>
                                                </div>
                                            </div></>
                                    }
                                    <div className="row">
                                        <div className="col-12 subheader">
                                            <h6 className={!!this.state.fileStatusReport.fileHistory ? "on" : "off"}>File history info</h6>
                                        </div>
                                    </div>
                                    {this.state.fileStatusReport.fileHistory &&
                                        <div className="row">
                                            <div className="col-4">
                                                <span className="label">Downloaded: </span>
                                                <span> {moment(this.state.fileStatusReport.fileHistory.downloadDateTime).format(dateFormats.fullFormat)}</span>
                                            </div>
                                            <div className="col-4">
                                                <span className="label">Dispatched: </span>
                                                <span> {this.state.fileStatusReport.fileHistory.dispatched ?
                                                    moment(this.state.fileStatusReport.fileHistory.downloadDateTime).format(dateFormats.fullFormat) : ""}</span>
                                            </div>
                                        </div>
                                    }
                                    <div className="row">
                                        <div className="col-12 subheader">
                                            <h6 className={!!this.state.fileStatusReport.fileStatus ? "on" : "off"}>Data loading info</h6>
                                        </div>
                                    </div>
                                    {this.state.fileStatusReport.fileStatus &&
                                        <div className="row">
                                            <div className="col-4">
                                                <div><span className="label">Started time: </span><span>{moment(this.state.fileStatusReport.fileStatus.startedDateTime).format(dateFormats.fullFormat)}</span></div>
                                                <div><span className="label">Last updated: </span><span>{moment(this.state.fileStatusReport.fileStatus.updatedDateTime).format(dateFormats.fullFormat)}</span></div>
                                                <div><span className="label">Retries: </span><span> {this.state.fileStatusReport.fileStatus.retries}</span></div>
                                                <div><span className="label">Elapsed: </span><span> {this.state.fileStatusReport.fileStatus.elapsed}</span></div>
                                            </div>
                                            <div className="col-4">
                                                <div><span className="label">Rows inserted: </span><span>{this.state.fileStatusReport.fileStatus.inserted}</span></div>
                                                <div><span className="label">Rows updated: </span><span>{this.state.fileStatusReport.fileStatus.updated}</span></div>
                                                <div><span className="label">Rows rejected: </span><span>{this.state.fileStatusReport.fileStatus.rejected}</span></div>
                                                <div><span className="label">Total records: </span><span>{this.state.fileStatusReport.fileStatus.committed}</span></div>
                                            </div>
                                        </div>
                                    }
                                    <div className="row">
                                        <div className="col-12 subheader">
                                            <h6 className={!!this.state.fileStatusReport.fileArchive ? "on" : "off"}>File archive info</h6>
                                        </div>
                                    </div>
                                    {this.state.fileStatusReport.fileArchive &&
                                        <div className="row">
                                            <div className="col-4">
                                                <span className="label">Archived: </span><span> {moment(this.state.fileStatusReport.fileArchive.timestamp).format(dateFormats.fullFormat)}</span>
                                            </div>
                                            <div className="col-4">
                                                <span className="label">Archived path: </span><span> {this.state.fileStatusReport.fileArchive.path}</span>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                            <div className="section-body row">
                                <div className="col-12 one-off-loader">
                                    <div className="row section-header">
                                        <h6>
                                            One-Off Data Load
                                        </h6>
                                    </div>
                                    <div className="row section-body col-12">
                                        <div className="col-4">
                                            <label htmlFor="filePath">Path:</label>/gdl/
                                            <Input id="filePath" name="path"
                                                value={this.state.dataLoaderInput.path}
                                                required={true}
                                                onChange={this.changeTextValue}
                                                title="File path" />
                                        </div>
                                        <div className="col-8 fileName">
                                            <label htmlFor="fileName">Name:</label>
                                            <Input id="fileName" name="name"
                                                value={this.state.dataLoaderInput.name}
                                                required={true}
                                                onChange={this.changeFilename}
                                                title="File name" />
                                        </div>
                                    </div>
                                    <div className="row section-body">
                                        <div className="col-4">
                                            <Checkbox
                                                id="ignoreCheckArchive"
                                                name="ignoreArchiveCheck"
                                                value={this.state.dataLoaderInput.ignoreArchiveCheck}
                                                onChange={this.changeBooleanValue}
                                                title="Ignore Archive existence"
                                                label="Ignore Archive checking" />
                                        </div>
                                        <div className="col-8 toolbar-right">
                                            <Button
                                                title="Start One-Off Data Loader"
                                                onClick={this.startOneOffDataLoader}
                                                icon="play"
                                                className="cmd start-service"
                                                type="button"
                                            >Start One-Off Loader</Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {this.state.confirmVisible && (
                            <Dialog title={this.state.confirmTitle} onClose={() => this.toggleConfirm()}>
                                <p
                                    style={{
                                        margin: "25px",
                                        textAlign: "center",
                                    }}
                                >
                                    Are you sure you want to continue?
                                </p>
                                <DialogActionsBar>
                                    <button className="k-button" onClick={() => this.toggleConfirm()}>
                                        No
                                    </button>
                                    <button className="k-button" onClick={this.doConfirm}>
                                        Yes
                                    </button>
                                </DialogActionsBar>
                            </Dialog>
                        )}
                    </div>
                </TabStripTab>
                <TabStripTab title="MULE GDL">
                    <div className="service-manager row">
                        {this.state.loading && <WaitLoading />}
                        <div className="col-7 file-status">
                            <div className="col-12">
                                <Grid
                                    data={this.state.latestMuleFileStatusView}
                                    className="file-status-grid"
                                    resizable={true}
                                    filterable={true}
                                    filter={this.state.filter}
                                    onFilterChange={this.onFilterChange}
                                    sortable={true}
                                    sort={this.state.sort}
                                    onSortChange={this.sortGrid}
                                >
                                    <GridToolbar>
                                        <div className="section-header">
                                            <h6>Latest File Status</h6>
                                            <div className="toolbar-right">
                                                <label className="toolbar-label">Show</label>
                                                <DropDownList
                                                    onChange={this.changeMuleDayValue}
                                                    name="muleFileStatusDays"
                                                    textField="name"
                                                    value={this.getDropdownValue(FileStatusDays_Options, this.state.fileStatusDays)}
                                                    data={FileStatusDays_Options}
                                                    className="days-drop-down"
                                                />
                                                <Button
                                                    title="Refresh list"
                                                    onClick={() => this.refreshMuleAuditTrackerStatus()}
                                                    icon="refresh"
                                                    svgIcon={arrowsClockwiseIcon}
                                                    className="cmd refresh"
                                                    type="button"></Button>
                                            </div>
                                        </div>
                                    </GridToolbar>
                                    <GridColumn field="partitionKey" title="Source" className="srcKey" headerClassName="srcKey" resizable sortable />
                                    <GridColumn field="fileName" title="File name" className="filename" headerClassName="filename" resizable sortable cells={{ data: this.textCell() }} />
                                    <GridColumn field="dbWriteStatus" title="Status" className="status" headerClassName="status" resizable sortable />
                                    <GridColumn field="timestamp" title="Last update" className="dateTime" headerClassName="dateTime" resizable sortable cells={{ data: this.dateCell("yyyy-MM-DD HH:mm") }} />
                                    <GridColumn field="recordsInserted" title="Inserted" className="text-align-right recNum" headerClassName="recNum" resizable filterable={false} sortable />
                                    <GridColumn field="recordsUpdated" title="Updated" className="text-align-right recNum" headerClassName="recNum" resizable filterable={false} sortable />
                                    <GridColumn field="recordsFailed" title="Rejected" className="text-align-right rejNum" headerClassName="rejNum" resizable filterable={false} sortable />
                                    <GridColumn field="totalRecords" title="Total" className="text-align-right recNum" headerClassName="recNum" resizable filterable={false} sortable />
                                    <GridColumn field="lastUpdateDateTime" title="Last Update" className="dateTime" headerClassName="dateTime" resizable sortable cells={{ data: this.dateCell("yyyy-MM-DD HH:mm") }} />
                                </Grid>
                            </div>
                        </div>
                        <div className="col-5">
                            <div className="section-body row">
                                <div className="col-12 one-off-loader">
                                    <div className="row section-header">
                                        <h6>
                                            One-Off Data Load
                                        </h6>
                                    </div>
                                    <div className="row section-body">
                                        <div className="col-4">
                                            <label htmlFor="filePath">Path:</label>
                                            <Input id="filePath" name="path"
                                                value={this.state.dataLoaderInput.path}
                                                required={true}
                                                onChange={this.changeTextValue}
                                                title="File path" />
                                        </div>
                                        <div className="col-8 fileName">
                                            <label htmlFor="fileName">Name:</label>
                                            <Input id="fileName" name="name"
                                                value={this.state.dataLoaderInput.name}
                                                required={true}
                                                onChange={this.changeFilename}
                                                title="File name" />
                                        </div>
                                    </div>
                                    <div className="row section-body">
                                        <div className="col-11 toolbar-right pt-1">
                                            <Button
                                                title="Start One-Off Data Loader"
                                                onClick={this.startMuleDataLoader}
                                                icon="play"
                                                className="cmd start-service"
                                                type="button"
                                            >Start One-Off Loader</Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                </TabStripTab>
            </TabStrip>);
    }

    private inquireRecord = async (filename: string) => {
        this.setState({
            checkFileName: filename
        });

        await this.checkFileStatus(filename);
    }

    private deleteRecord = async (partitionKey: string, rowKey: string) => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        this.setState({
            loading: true
        });
        const recordToBeDeleted = { partitionKey, rowKey };
        Server.deleteJson(`${apiUrls.deleteRunningFile}?partitionKey=${partitionKey}&rowKey=${rowKey}`, apiAccessToken, JSON.stringify(recordToBeDeleted))
            .then(() => {
                this.refreshRunningFiles();
                this.setState({
                    loading: false
                });
                AppAlert.Success(`Record deleted sucessfully.`);
            })
            .catch((e) => {
                console.log("Exception deleting record from WorkingFile table: " + e);
                this.setState({
                    loading: false
                });
            });
    }

    private oneOffLoad = async () => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        this.setState({
            loading: true
        });
        Server.postJson(apiUrls.oneOffLoad, apiAccessToken, JSON.stringify(this.state.dataLoaderInput))
            .then(() => {
                AppAlert.Success(`One-Off loader started sucessfully.`);

                this.setState({
                    loading: false,
                });


            })
            .catch((e) => {
                console.log("Exception starting One-Off loader: " + e);
                this.setState({
                    loading: false
                });
            });
    }

    private setPropertyValue(obj: any, property: string, newValue: any) {
        const props = property.split(".");
        let ds_prop: any = obj;
        for (let i = 0; i < props.length - 1; i++) {
            ds_prop = ds_prop[props[i]];
        }
        const oldValue = ds_prop[props[props.length - 1]];
        ds_prop[props[props.length - 1]] = newValue;
        return oldValue !== newValue;
    }

    private changeTextValue = (event: any) => {
        const dataLoaderInput = this.state.dataLoaderInput;
        if (this.setPropertyValue(dataLoaderInput, event.target.name, event.target.value))
            this.setState({ dataLoaderInput });
    }

    private changeCheckFileName = (event: any) => {
        this.setState({ checkFileName: event.target.value });
    }

    private changeFilename = (event: any) => {
        const dataLoaderInput = this.state.dataLoaderInput;
        let filename: string = event.target.value;
        if (filename.indexOf("/") !== -1) {
            const path: string = filename.substring(0, filename.lastIndexOf("/"));
            filename = filename.substring(filename.lastIndexOf("/") + 1);
            dataLoaderInput.path = path;
        }
        dataLoaderInput.name = filename;
        this.setState({ dataLoaderInput });
    }

    private changeBooleanValue = (event: CheckboxChangeEvent) => {
        const dataLoaderInput = this.state.dataLoaderInput;
        if (this.setPropertyValue(dataLoaderInput, event.target.element!.name, event.value))
            this.setState({ dataLoaderInput });
    }

    private startOneOffDataLoader = () => {
        this.oneOffLoad();
    }

    private startMuleDataLoader = () => {
        this.oneOffMuleLoad();
    }

    private oneOffMuleLoad = async () => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        this.setState({
            loading: true
        });
        Server.postJson(apiUrls.oneOffMuleLoad, apiAccessToken, JSON.stringify(this.state.dataLoaderInput))
            .then(() => {
                AppAlert.Success(`One-Off loader started sucessfully.`);

                this.setState({
                    loading: false,
                });


            })
            .catch((e) => {
                console.log("Exception starting One-Off loader: " + e);
                this.setState({
                    loading: false
                });
            });
    }

    private isServiceDisabled = async (serviceName: string): Promise<boolean> => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        this.setState({
            loading: true
        });
        const result: IServiceStateOutput =
            await Server.getJson(`${apiUrls.checkServiceDisabled}?serviceName=${serviceName}`, apiAccessToken);
        return result.state === IServiceState.Disabled;
    }


    private getLatestFileStatus = async (days: number): Promise<IFileStatus[]> => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        this.setState({
            loading: true
        });
        const result: IFileStatus[] =
            await Server.getJson(`${apiUrls.latestFileStatusList}?days=${days}`, apiAccessToken);
        result.forEach(item => {
            item.elapsed = this.getElapsed(item.updatedDateTime, item.startedDateTime);
        });
        return result;
    }

    private getRunningFiles = async (): Promise<IWorkingFile[]> => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        const result: IWorkingFile[] =
            await Server.getJson(apiUrls.getRunningFiles, apiAccessToken);
        return result;
    }

    private setServiceDisabled = async (serviceName: string, isDisabled: boolean): Promise<boolean> => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        this.setState({
            loading: true
        });
        return Server.postJson(`${apiUrls.setServiceDisabled}?serviceName=${serviceName}&disabled=${isDisabled}`, apiAccessToken)
            .then(() => {
                AppAlert.Success(`Service ${serviceName} ${isDisabled ? "disabled" : "enabled"} successfully.`);
                return true;
            })
            .catch((e) => {
                console.log("Exception changing service status: " + e);
                return false;
            });
    }

    private setSettingValueDisabled = async (key: string, isDisabled: boolean): Promise<boolean> => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        this.setState({
            loading: true
        });
        return Server.postJson(`${apiUrls.setSettingValue}?key=${key}&value=${isDisabled ? "1" : "0"}`, apiAccessToken)
            .then(() => {
                AppAlert.Success(`Setting ${key} ${isDisabled ? "disabled" : "enabled"} successfully.`);
                return true;
            })
            .catch((e) => {
                console.log("Exception changing settings value: " + e);
                return false;
            });
    }

    private toggleFileLoaderDisabled = async () => {
        this.setState({
            loading: true
        });
        const fileLoaderDisabled = !this.state.fileLoaderDisabled;
        const done = await this.setServiceDisabled("FileLoader", fileLoaderDisabled);
        if (done) {
            this.setState({
                fileLoaderDisabled,
                loading: false
            });
        } else {
            this.setState({
                loading: false
            });
        }
    }

    private toggleSettingsDisabled = async (key: string) => {
        this.setState({
            loading: true
        });
        const settingsValues = this.state.settingsValues;
        settingsValues[key] = !settingsValues[key];

        const done = await this.setSettingValueDisabled(key, settingsValues[key]);
        if (done) {
            this.setState({
                settingsValues,
                loading: false
            });
        } else {
            this.setState({
                loading: false
            });
        }
    }

    private toggleDataLoaderDisabled = async () => {
        this.setState({
            loading: true
        });
        const dataLoaderDisabled = !this.state.dataLoaderDisabled;
        const done = await this.setServiceDisabled("DaatLoader", dataLoaderDisabled);
        if (done) {
            this.setState({
                dataLoaderDisabled,
                loading: false
            });
        } else {
            this.setState({
                loading: false
            });
        }
    }

    private refreshFileStatus = async (days?: number) => {
        this.setState({
            loading: true
        });

        let { latestFileStatus, latestFileStatusView, fileStatusDays, filter, sort } = this.state;
        try {
            latestFileStatus = await this.getLatestFileStatus(days ?? fileStatusDays);

            if (filter && filter.filters.length)
                latestFileStatusView = filterBy(latestFileStatus, filter);
            else
                latestFileStatusView = latestFileStatus.slice();

            if (sort.length)
                latestFileStatusView = orderBy(latestFileStatusView, sort);

        } catch (e) {
        }

        this.setState({
            loading: false,
            latestFileStatus,
            latestFileStatusView
        });

    }

    private refreshRunningFiles = async () => {
        let { runningFiles } = this.state;

        this.setState({
            loading: true
        });

        try {
            runningFiles = await this.getRunningFiles();
        } catch (e) {
        }

        this.setState({
            runningFiles,
            loading: false
        });
    }

    private refreshStatus = async () => {
        this.setState({
            loading: true
        });
        let { fileLoaderDisabled, dataLoaderDisabled, settingsValues } = this.state;
        try {
            fileLoaderDisabled = await this.isServiceDisabled("FileLoader");
        } catch (e) {
            AppAlert.Error("Error getting file extractor status!");
        }
        try {
            dataLoaderDisabled = await this.isServiceDisabled("DataLoader");
        } catch (e) {
            AppAlert.Error("Error getting data loader status!");
        }
        try {
            settingsValues[settingsKeys.ftpExtractorDisabled] = await this.getSettingValue(settingsKeys.ftpExtractorDisabled);
        } catch (e) {
            AppAlert.Error("Error getting ftp extractor status!");
        }
        try {
            settingsValues[settingsKeys.httpExtractorDisabled] = await this.getSettingValue(settingsKeys.httpExtractorDisabled);
        } catch (e) {
            AppAlert.Error("Error getting http extractor status!");
        }
        try {
            settingsValues[settingsKeys.folderExtractorDisabled] = await this.getSettingValue(settingsKeys.folderExtractorDisabled);
        } catch (e) {
            AppAlert.Error("Error getting folder extractor status!");
        }
        try {
            settingsValues[settingsKeys.sftpExtractorDisabled] = await this.getSettingValue(settingsKeys.sftpExtractorDisabled);
        } catch (e) {
            AppAlert.Error("Error getting sftp extractor status!");
        }

        this.setState({
            loading: false,
            fileLoaderDisabled,
            dataLoaderDisabled,
            settingsValues
        });
    }

    private refreshAll = async () => {

        await this.refreshStatus();

        await this.refreshFileStatus();

        await this.refreshRunningFiles();
    }

    private sortGrid = (e: GridSortChangeEvent) => {
        const latestFileStatusView = orderBy(this.state.latestFileStatusView, e.sort);

        this.setState({
            latestFileStatusView,
            sort: e.sort
        });
    }

    private onSortChangeR = (e: GridSortChangeEvent) => {
        this.setState({
            rSort: e.sort
        });
    }

    private onFilterChange = (e: any) => {
        const { sort } = this.state;
        console.log(e.filter);
        let latestFileStatusView = filterBy(this.state.latestFileStatus, e.filter);
        if (sort.length)
            latestFileStatusView = orderBy(latestFileStatusView, sort);

        this.setState({
            latestFileStatusView,
            filter: e.filter
        });
    }

    private onFilterChangeR = (e: any) => {
        this.setState({
            rFilter: e.filter
        });
    }

    private commandCell = () => {
        const toggleConfirm = this.toggleConfirm;
        const deleteRecord = this.deleteRecord;
        const inquireRecord = this.inquireRecord;
        const copyFilename = (filename: string) => {
            const dataLoaderInput = this.state.dataLoaderInput;
            const path = filename.substr(0, filename.lastIndexOf("/"))
            const name = filename.substr(filename.lastIndexOf("/") + 1)
            dataLoaderInput.name = name;
            dataLoaderInput.path = path;
            this.setState({ dataLoaderInput })
        }
        return class extends React.Component<GridCellProps> {
            public render() {
                const { partitionKey, rowKey, filename } = this.props.dataItem;

                return (
                    <td>
                        <Button
                            title="Check working file status"
                            onClick={() => inquireRecord(filename)}
                            icon="question"
                            svgIcon={ questionCircleIcon}
                            className="cmd inquire-record"
                            type="button"
                        ></Button>
                        &nbsp;
                        <Button
                            title="Copy file name to one-off loader"
                            onClick={() => copyFilename(filename)}
                            icon="copy"
                            svgIcon={copyIcon}
                            className="cmd copy-filename"
                            type="button"
                        ></Button>
                        &nbsp;
                        <Button
                            title="Delete working file record "
                            onClick={() => toggleConfirm("Delete record from WorkingFile table",
                                () => deleteRecord(partitionKey, rowKey))}
                            icon="delete"
                            svgIcon={trashIcon}
                            className="cmd delete-record"
                            type="button"
                        ></Button>
                    </td>
                );
            }
        }
    }
    public static getFileSize(length: number): string {
        return length >= 1024 * 1024
            ? `${Math.floor(length / (1024 * 1024)).toLocaleString("en-US")} MB`
            : (length >= 1024
                ? `${Math.floor(length / 1024)} KB`
                : `${length} B`);
    }

    private sizeCell = () => {
        return class extends React.Component<GridCellProps> {
            public render() {
                const dataItem = this.props.dataItem;
                const length: number = dataItem[this.props.field!];
                return (
                    <td className={this.props.className} style={this.props.style} title={`${length} bytes`}>
                        {ServiceManager.getFileSize(length)}
                    </td>
                );
            }
        }
    }

    private textCell = () => {
        return class extends React.Component<GridCellProps> {
            public render() {
                const dataItem = this.props.dataItem;
                const txt: string = dataItem[this.props.field!] ?? "";
                return (
                    <td className={this.props.className} style={this.props.style} title={txt}>
                        {txt}
                    </td>
                );
            }
        }
    }

    private dateCell = (format?: string) => {
        return class extends React.Component<GridCellProps> {
            public render() {
                if (!format)
                    format = dateFormats.fullFormat;
                const dataItem = this.props.dataItem;
                //const value = this.props.field ? moment(dataItem[this.props.field]).format("yyyy-MM-DD HH:mm") : '';
                const value = this.props.field ? moment(dataItem[this.props.field]).format(format) : '';
                let hint = "";
                if (format !== dateFormats.fullFormat)
                    hint = this.props.field ? moment(dataItem[this.props.field]).format(dateFormats.fullFormat) : '';
                return (
                    <td className={this.props.className} style={this.props.style} title={hint}>
                        {value}
                    </td>
                );
            }
        }
    }

    private checkAutoRefresh = (autoRefresh: boolean) => {
        if (autoRefresh) {
            const intervalId = setInterval(this.refreshAll, RefreshStatusInterval).valueOf();
            this.setState({
                intervalId: intervalId,
            });
        } else {
            if (this.state.intervalId)
                clearInterval(this.state.intervalId);
        }
    }

    private toggleAutoRefresh = async () => {
        const autoRefresh = !this.state.autoRefresh;
        this.checkAutoRefresh(autoRefresh);
        this.setState({
            autoRefresh
        });
    }

    private getDropdownValue(day_options: { name: string, value: any }[], value: string | number): IDropdownData {
        return { name: day_options.find(x => x.value === value)!.name, value };
    }

    private changeDayValue = async (event: any) => {
        const fileStatusDays = event.target.value.value;
        this.setState({
            loading: true
        });
        await this.refreshFileStatus(fileStatusDays);
        this.setState({
            fileStatusDays,
            loading: false
        });
    }

    private checkFileStatus = async (filename?: string) => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        this.setState({
            loading: true
        });
        const fileStatusReport: ICheckFileStatus =
            await Server.getJson(`${apiUrls.checkFileStatus}?filename=${filename ?? this.state.checkFileName}`, apiAccessToken);
        if (fileStatusReport.fileStatus) {
            fileStatusReport.fileStatus.elapsed = this.getElapsed(fileStatusReport.fileStatus.updatedDateTime,
                fileStatusReport.fileStatus.startedDateTime);
        }
        this.setState({
            loading: false,
            fileStatusReport
        });
    }

    private toggleConfirm = (confirmTitle: string = "", confirmAction: any = null) => {
        this.setState({
            confirmAction,
            confirmTitle,
            confirmVisible: !this.state.confirmVisible
        });
    }

    private doConfirm = () => {
        if (this.state.confirmAction) {
            this.state.confirmAction();
            this.setState({
                confirmVisible: false
            });
        }
    }

    private getElapsed(date1: Date, date2: Date) {
        var diff = moment(date1).diff(moment(date2));
        var d = moment.duration(diff);
        if (d.as('seconds') < 1)
            return `0. ${String(d.as('milliseconds')).padStart(3, '0')} ms`;
        else
            return `${Math.trunc(d.as('days'))}.${moment.utc(diff).format('HH:mm:ss')}`;
    }

    private getSettingValue = async (key: string): Promise<boolean> => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        const secret: any =
            await Server.getJson(`${apiUrls.getSettingValue}?key=${key}`, apiAccessToken);
        return secret.value === "1";
    }

    private handleTabSelect = (e: TabStripSelectEventArguments) => {
        this.setState({
            tabSelected: e.selected
        });
    };

    private refreshMuleAuditTrackerStatus = async (days?: number) => {
        this.setState({
            loading: true
        });

        let { latestMuleFileStatus, latestMuleFileStatusView, fileStatusDays, filter, sort } = this.state;
        try {
            latestMuleFileStatus = await this.getMuleLatestFileStatus(days ?? fileStatusDays);

            if (filter && filter.filters.length)
                latestMuleFileStatusView = filterBy(latestMuleFileStatus, filter);
            else
                latestMuleFileStatusView = latestMuleFileStatus.slice();

            if (sort.length)
                latestMuleFileStatusView = orderBy(latestMuleFileStatusView, sort);

        } catch (e) {
        }

        this.setState({
            loading: false,
            latestMuleFileStatus,
            latestMuleFileStatusView
        });

    }

    private getMuleLatestFileStatus = async (days: number): Promise<IAuditTracker[]> => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        this.setState({
            loading: true
        });
        const result: IAuditTracker[] =
            await Server.getJson(`${apiUrls.latestAuditTracker}?days=${days}`, apiAccessToken);
        return result;
    }

    private changeMuleDayValue = async (event: any) => {
        const fileStatusDays = event.target.value.value;
        this.setState({
            loading: true
        });
        await this.refreshMuleAuditTrackerStatus(fileStatusDays);
        this.setState({
            fileStatusDays,
            loading: false
        });
    }
}

